/**
 * QUIPWA — FSRS-4.5 Algorithm
 *
 * Pure implementation. No side effects. No imports.
 * Reference: https://github.com/open-spaced-repetition/fsrs4anki
 *
 * Variables:
 *   S — stability: days until 90% recall probability
 *   D — difficulty: intrinsic card difficulty (1–10)
 *   R — retrievability: current recall probability, computed at runtime
 */

// ─── CONSTANTS ───────────────────────────────────────────────────────────────

/** FSRS-4.5 reference weights (w[0..16]) */
export const DEFAULT_W: readonly number[] = [
  0.4072, 1.1829, 3.1262, 15.4722,  // w[0..3]: S_0 for ratings 1-4
  7.2102, 0.5316, 1.0651, 0.0589,   // w[4..7]
  1.5330, 0.1544, 0.9434, 1.9813,   // w[8..11]
  0.1100, 0.2900, 2.2700, 0.0700,   // w[12..15]
  2.9898                             // w[16]
] as const;

export const FSRS_DESIRED_RETENTION = 0.9;
export const MAX_INTERVAL = 36500;       // 100 years
export const LEECH_THRESHOLD = 8;

const DECAY = -0.5;
/** ≈ 19/81 */
const FACTOR = Math.pow(0.9, 1 / DECAY) - 1;

// ─── HELPER ──────────────────────────────────────────────────────────────────

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

// ─── CORE FORMULAS ───────────────────────────────────────────────────────────

/**
 * Current retrievability R given elapsed days and stability S.
 * R = (1 + FACTOR * elapsed / S) ^ DECAY
 */
export function retrievability(elapsedDays: number, stability: number): number {
  if (stability <= 0) return 0;
  return Math.pow(1 + FACTOR * elapsedDays / stability, DECAY);
}

/**
 * Optimal interval (days) to reach desiredRetention.
 */
export function nextInterval(
  stability: number,
  desiredRetention: number = FSRS_DESIRED_RETENTION
): number {
  const raw = stability / FACTOR * (Math.pow(desiredRetention, 1 / DECAY) - 1);
  return Math.min(Math.round(raw), MAX_INTERVAL);
}

/**
 * Initial stability for a new card based on its first rating.
 * Maps: rating 1→w[0], 2→w[1], 3→w[2], 4→w[3]
 */
export function initialStability(rating: 1 | 2 | 3 | 4 | 5, w: readonly number[] = DEFAULT_W): number {
  const baseRating = (rating === 5 ? 4 : rating) as 1 | 2 | 3 | 4;
  const s = w[baseRating - 1];
  return rating === 5 ? s * 1.25 : s;
}

/**
 * Initial difficulty for a new card.
 */
export function initialDifficulty(rating: 1 | 2 | 3 | 4 | 5, w: readonly number[] = DEFAULT_W): number {
  const baseRating = (rating === 5 ? 4 : rating) as 1 | 2 | 3 | 4;
  return clamp(w[4] - Math.exp(w[5] * (baseRating - 1)) + 1, 1, 10);
}

/**
 * Updated difficulty after a review.
 */
export function nextDifficulty(d: number, rating: 1 | 2 | 3 | 4 | 5, w: readonly number[] = DEFAULT_W): number {
  const baseRating = (rating === 5 ? 4 : rating) as 1 | 2 | 3 | 4;
  const delta = -w[6] * (baseRating - 3);
  return clamp(d + delta * ((10 - d) / 9), 1, 10);
}

/**
 * New stability after a successful recall (rating >= 2).
 */
export function nextStabilityRecall(
  s: number,
  d: number,
  r: number,
  rating: 2 | 3 | 4 | 5,
  w: readonly number[] = DEFAULT_W
): number {
  const hardPenalty = rating === 2 ? w[15] : 1;
  const easyBonus   = rating >= 4 ? w[16] : 1;
  const perfectBonus = rating === 5 ? 1.25 : 1; // 25% extra for "Perfecto"

  return s * (
    Math.exp(w[8]) * (11 - d)
    * Math.pow(s, -w[9])
    * (Math.exp((1 - r) * w[10]) - 1)
    * hardPenalty * easyBonus * perfectBonus
    + 1
  );
}

/**
 * New stability after a lapse (rating === 1).
 */
export function nextStabilityForgot(
  s: number,
  d: number,
  r: number,
  w: readonly number[] = DEFAULT_W
): number {
  return w[11]
    * Math.pow(d, -w[12])
    * (Math.pow(s + 1, w[13]) - 1)
    * Math.exp((1 - r) * w[14]);
}

// ─── SCHEDULE RESULT ─────────────────────────────────────────────────────────

export interface FSRSUpdate {
  stability: number;
  difficulty: number;
  scheduled_days: number;
  elapsed_days: number;
  state: 0 | 1 | 2 | 3;   // new/learning/review/relearning
  reps: number;
  lapses: number;
}

export interface ReviewStateInput {
  stability: number;
  difficulty: number;
  elapsed_days: number;
  scheduled_days: number;
  reps: number;
  lapses: number;
  state: 0 | 1 | 2 | 3;
}

// ─── MAIN SCHEDULING FUNCTION ────────────────────────────────────────────────

/**
 * Compute the next FSRS state after a review.
 *
 * @param rs      Current ReviewState snapshot
 * @param rating  User rating: 1=Again, 2=Hard, 3=Good, 4=Easy (5 mapped to 4 before calling)
 * @param desiredRetention  Default 0.9
 * @param w       FSRS weights (defaults to DEFAULT_W)
 * @returns       New state values to persist in review_state
 */
export function scheduleCard(
  rs: ReviewStateInput,
  rating: 1 | 2 | 3 | 4 | 5,
  desiredRetention: number = FSRS_DESIRED_RETENTION,
  w: readonly number[] = DEFAULT_W
): FSRSUpdate {
  const r = rs.reps === 0
    ? 0
    : retrievability(rs.elapsed_days, rs.stability);

  let s: number;
  let d: number;

  if (rs.state === 0) {
    // New card: initialize S and D from rating
    s = initialStability(rating, w);
    d = initialDifficulty(rating, w);
  } else if (rating === 1) {
    // Lapse
    s = nextStabilityForgot(rs.stability, rs.difficulty, r, w);
    d = nextDifficulty(rs.difficulty, rating, w);
  } else {
    // Recall
    s = nextStabilityRecall(rs.stability, rs.difficulty, r, rating as 2 | 3 | 4 | 5, w);
    d = nextDifficulty(rs.difficulty, rating, w);
  }

  const interval = Math.min(nextInterval(s, desiredRetention), MAX_INTERVAL);
  const newState: 0 | 1 | 2 | 3 = rating === 1 ? 3 : (rs.state === 0 ? 1 : 2);

  return {
    stability: s,
    difficulty: d,
    scheduled_days: interval,
    elapsed_days: 0,        // reset after review
    state: newState,
    reps: rs.reps + 1,
    lapses: rating === 1 ? rs.lapses + 1 : rs.lapses
  };
}

// ─── RATING MAPPING ──────────────────────────────────────────────────────────

/**
 * Map 1-5 UI ratings to FSRS 1-4.
 * UI: 1=Nada, 2=Difícil, 3=Bien, 4=Fácil, 5=Perfecto
 * FSRS: 1=Again, 2=Hard, 3=Good, 4=Easy
 */
export function mapUIRatingToFSRS(uiRating: 1 | 2 | 3 | 4 | 5): 1 | 2 | 3 | 4 {
  return uiRating === 5 ? 4 : uiRating;
}

// ─── DUE DATE COMPUTATION ────────────────────────────────────────────────────

/**
 * Unix timestamp (seconds) of the next due date.
 * @param scheduledDays  Interval in days from FSRS
 * @param fromTs         Base timestamp (seconds). Defaults to now.
 */
export function nextDueTimestamp(scheduledDays: number, fromTs?: number): number {
  const base = fromTs ?? Math.floor(Date.now() / 1000);
  return base + scheduledDays * 86400;
}

// ─── SESSION QUEUE HELPERS ───────────────────────────────────────────────────

/**
 * Estimate total session time in minutes given a card count and
 * target seconds per card.
 */
export function estimateSessionMinutes(
  cardCount: number,
  targetSecondsPerCard = 20
): number {
  return Math.ceil((cardCount * targetSecondsPerCard) / 60);
}
